// Centralized API module for all backend calls
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    // Special handling for quota/rate limit errors (HTTP 429)
    if (response.status === 429) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, show a generic quota message
        throw new Error(`Rate Limit Exceeded\n\nYou've made too many requests. Please wait a minute and try again.\n\nTip: Upgrade your subscription for higher limits!`);
      }
      
      // Successfully parsed error data - build detailed message
      const limitType = errorData?.limit_type === 'monthly' ? 'Monthly Quota' : 'Rate Limit';
      const retryAfter = response.headers.get('Retry-After');
      const tier = errorData?.subscription_tier || 'current plan';
      
      // Build user-friendly message
      let quotaMessage = `${limitType} Exceeded\n\n`;
      quotaMessage += errorData?.detail || `You've exceeded the ${limitType.toLowerCase()} for the ${tier}.`;
      
      // Add usage stats
      if (errorData?.current_usage && errorData?.limit) {
        quotaMessage += `\n\nUsage: ${errorData.current_usage}/${errorData.limit} requests`;
      }
      
      // Add retry information
      if (retryAfter) {
        const retrySeconds = parseInt(retryAfter);
        if (retrySeconds < 60) {
          quotaMessage += `\n\nPlease wait ${retrySeconds} seconds before trying again.`;
        } else {
          const minutes = Math.ceil(retrySeconds / 60);
          quotaMessage += `\n\nPlease wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`;
        }
      }
      
      // Add upgrade suggestion + plan details
      const recommendedPlan = errorData?.reccomended_plan || errorData?.recommended_plan || errorData?.next_tier;
      quotaMessage += `\n\nTip: Upgrade your subscription for higher limits!`;
      if (recommendedPlan) {
        quotaMessage += `\nRecommended plan: ${recommendedPlan}`;
      }
    
      
      const err = new Error(quotaMessage);
      err.limitType = limitType;
      err.subscriptionTier = tier;
      err.recommendedPlan = recommendedPlan || null;
      throw err;
    }
    
    // Handle other errors
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData?.detail;
    let message = undefined;
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      // FastAPI validation errors array
      message = detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
    } else if (detail && typeof detail === 'object') {
      message = JSON.stringify(detail);
    }
    throw new Error(message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Automatically include auth token if available
  const token = getAuthToken();
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),  // Include token if user is logged in
      ...options.headers,
    },
    ...options,
  };

  // Remove Content-Type for FormData requests
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  try {
    const response = await fetch(url, config);
    return handleResponse(response);
  } catch (networkError) {
    // Network error (server down, CORS issue, etc.)
    console.error('Network error:', networkError);
    throw new Error(`Network Error\n\nCould not connect to the server. Please check if the backend is running.\n\nError details: ${networkError.message}`);
  }
};

// Dataset-related API calls
export const datasetsAPI = {
  // Get all available datasets
  getDatasets: () => apiRequest('/omics/datasets'),

  // Preview a dataset
  previewDataset: (datasetName, rows = 10) => 
    apiRequest(`/omics/${datasetName}/preview?rows=${rows}`),

  // Explore a dataset
  exploreDataset: (datasetName) => 
    apiRequest(`/omics/${datasetName}/explore`, { method: 'POST' }),

  // Analyze correlations in a dataset
  analyzeCorrelations: (datasetName) => 
    apiRequest(`/omics/${datasetName}/correlations`, { method: 'POST' }),

  // Normalize data
  normalizeData: (datasetName, method) => 
    apiRequest(`/omics/${datasetName}/normalize?method=${method}`, { method: 'POST' }),

  // Delete a dataset
  deleteDataset: (datasetName) =>
    apiRequest(`/omics/${encodeURIComponent(datasetName)}`, { method: 'DELETE' }),

  // Rename a dataset
  renameDataset: (datasetName, newName) =>
    apiRequest(`/omics/${encodeURIComponent(datasetName)}/rename`, {
      method: 'PUT',
      body: JSON.stringify({ new_name: newName }),
    }),
};

// ML Model-related API calls
export const mlModelAPI = {
  // Validate training data
  validateTrainingData: (datasetName, targetColumn) => 
    apiRequest(`/omics/${datasetName}/validate?target_column=${targetColumn}`, { method: 'POST' }),

  // Train ML model. Optional body: { neural_network_hyperparameters, tuning_mode } for neural_network.
  trainMLModel: (datasetName, targetColumn, modelType, testSize, normalize, normalizationMethod, taskType, datasetType, body = null, datasetId = null) => {
    const params = new URLSearchParams({
      target_column: targetColumn,
      model_type: modelType,
      test_size: String(testSize),
      normalize: String(normalize),
      normalization_method: normalizationMethod,
      task_type: taskType,
    });
    if (datasetType) {
      params.append('dataset_type', datasetType);
    }
    if (datasetId != null) {
      params.append('dataset_id', String(datasetId));
    }
    const options = { method: 'POST' };
    if (body && typeof body === 'object' && Object.keys(body).length > 0) {
      options.body = JSON.stringify(body);
    }
    return apiRequest(`/omics/${datasetName}/ml-model?${params}`, options);
  },

  // Get feature importance
  getFeatureImportance: () => 
    apiRequest('/omics/results/feature-importance'),

  // Get model performance
  getModelPerformance: () => 
    apiRequest('/omics/results/model-performance'),
  
  getFeatureImportanceData: (datasetName, modelType) => 
    apiRequest(`/omics/results/feature-importance-data?dataset_name=${datasetName}&model=${modelType}`),

  getTargetScorecards: (datasetName, modelType) =>
    apiRequest(`/omics/results/target-scorecards?dataset_name=${encodeURIComponent(datasetName)}&model=${encodeURIComponent(modelType)}`),

  // Resolve the gene list for gene-based analyses (pathways, scorecards): the
  // trained model's features when they're genes, otherwise genes detected from
  // dataset rows (gene column alongside per-gene statistic columns).
  getEffectiveGenes: (datasetName, modelType, topN = 200) =>
    apiRequest(`/omics/${encodeURIComponent(datasetName)}/effective-genes?model=${encodeURIComponent(modelType || '')}&top_n=${topN}`),

  scoreTargets: (genes, featureImportance = null, efoId = 'MONDO_0004975') =>
    apiRequest('/target/score', {
      method: 'POST',
      body: JSON.stringify({
        genes,
        efo_id: efoId,
        feature_importance: featureImportance,
      }),
    }),
  
  getModelPerformanceData: (datasetName, modelType) => 
    apiRequest(`/omics/results/model-performance-data?dataset_name=${datasetName}&model=${modelType}`),
  
  // Get real-time training status
  getTrainingStatus: (datasetName, modelType) =>
    apiRequest(`/omics/training/status/${datasetName}/${modelType}`),

  // Model evaluation transparency: summary and flags by model ID
  getModelSummary: (datasetName, modelId) =>
    apiRequest(`/omics/metrics/model_summary/${encodeURIComponent(datasetName)}/${encodeURIComponent(modelId)}`),
  getEvaluationFlags: (datasetName, modelId) =>
    apiRequest(`/omics/metrics/evaluation_flags/${encodeURIComponent(datasetName)}/${encodeURIComponent(modelId)}`),

  // Run Unsupervised KMeans Clustering
  runKMeansClustering: (datasetName, nClusters) =>
    apiRequest(`/omics/${datasetName}/unsupervised/kmeans`, {
      method: 'POST',
      body: JSON.stringify({ n_clusters: nClusters })
    }),

  // Recommend optimal number of clusters (elbow method + silhouette analysis)
  recommendClusterK: (datasetName, kMin = 2, kMax = 10) =>
    apiRequest(`/omics/${datasetName}/unsupervised/recommend_k`, {
      method: 'POST',
      body: JSON.stringify({ k_min: kMin, k_max: kMax })
    }),
};

// Multi-omics related API calls
export const multiOmicsAPI = {
  // Analyze overlap between 2 datasets
  analyzeOverlap: (dataset1, dataset2) => 
    apiRequest(`/omics/multi-omics/overlap?dataset1_name=${encodeURIComponent(dataset1)}&dataset2_name=${encodeURIComponent(dataset2)}`, { method: 'POST' }),
  
  // Analyze correlations across multiple datasets
  analyzeMultiOmicsCorrelations: (dataset1, dataset2) => 
    apiRequest(`/omics/multi-omics/correlation?dataset1_name=${encodeURIComponent(dataset1)}&dataset2_name=${encodeURIComponent(dataset2)}`, { method: 'GET' }),
};

// Upload-related API calls
export const uploadAPI = {
  // Upload a file
  uploadFile: (file, file_type, project_name, preprocessing_applied) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', file_type);
    formData.append('project_name', project_name);
    formData.append('preprocessing_applied', preprocessing_applied);
    return apiRequest('/upload', {
      method: 'POST',
      body: formData,
    });
  },
};

//Get the projects
export const getProjects = async () => {
  // apiRequest handles your base URL, authorization headers, and response parsing
  return await apiRequest('/projects', {
    method: 'GET',
  });
};

// Compound-related API calls
export const compoundAPI = {
  // Get compound details
  getCompoundDetails: (compoundId) => 
    apiRequest(`/compound/${compoundId}`),
};

// Prediction-related API calls
export const predictAPI = {
  // Get predictions (legacy mock data endpoint)
  getPredictions: () => 
    apiRequest('/predict'),

  // Get the latest prediction results from the most recent batch prediction file
  getLatestPredictionResults: async () => {
    try {
      // Add cache-busting parameter to prevent browser caching
      const timestamp = Date.now();
      console.log('Fetching latest prediction results from:', `${API_BASE_URL}/outputs/latest?t=${timestamp}`);
      const response = await fetch(`${API_BASE_URL}/outputs/latest?t=${timestamp}`);
      if (response.ok) {
        const csvText = await response.text();
        console.log('Successfully fetched latest prediction results, parsing CSV...');
        const parsedData = parseCSVToPredictions(csvText);
        console.log('Parsed prediction data:', parsedData.length, 'records');
        return parsedData;
      } else {
        console.warn('Failed to fetch latest predictions, status:', response.status);
        // Fallback to mock data if no real data available
        return await apiRequest('/predict');
      }
    } catch (error) {
      console.error('Error fetching latest predictions:', error);
      return await apiRequest('/predict');
    }
  },

  // Get prediction results from a specific batch prediction file
  getPredictionResultsFromFile: async (filename) => {
    try {
      // Ensure filename has .csv extension
      const filenameWithExt = filename.endsWith('.csv') ? filename : `${filename}.csv`;
      // Add cache-busting parameter to prevent browser caching
      const timestamp = Date.now();
      console.log('Fetching specific prediction file:', `${API_BASE_URL}/outputs/${filenameWithExt}?t=${timestamp}`);
      const response = await fetch(`${API_BASE_URL}/outputs/${filenameWithExt}?t=${timestamp}`);
      if (response.ok) {
        const csvText = await response.text();
        console.log('Successfully fetched specific prediction file, parsing CSV...');
        const parsedData = parseCSVToPredictions(csvText);
        console.log('Parsed specific prediction data:', parsedData.length, 'records');
        return parsedData;
      } else {
        console.warn('Failed to fetch specific prediction file, status:', response.status);
        throw new Error(`File not found: ${filenameWithExt}`);
      }
    } catch (error) {
      console.error('Error fetching prediction file:', error);
      throw error;
    }
  },
};

// Helper function to parse CSV data into prediction format
const parseCSVToPredictions = (csvText) => {
  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',');
  
  
  // Find column indices - be more flexible with column detection
  const predictionIndex = headers.findIndex(h => h.toLowerCase().includes('prediction'));
  const probNegIndex = headers.findIndex(h => h.toLowerCase().includes('probability_negative'));
  const probPosIndex = headers.findIndex(h => h.toLowerCase().includes('probability_positive'));
  
  // The first column is always the compound/gene ID - use it exactly as is
  const patientIdIndex = 0;
  
  // Get feature columns (exclude metadata columns)
  const featureColumns = headers.filter((h, index) => 
    index !== predictionIndex && 
    index !== patientIdIndex &&
    index !== probNegIndex && 
    index !== probPosIndex
  );
  
  // Identify which columns contain text vs numeric data by checking the first few rows
  const textColumns = new Set();
  const numericColumns = new Set();
  
  // Check first 3 data rows to determine column types
  for (let i = 1; i <= Math.min(3, lines.length - 1); i++) {
    const values = lines[i].split(',');
    values.forEach((value, index) => {
      // Skip prediction and probability columns when determining types
      if (index !== predictionIndex && index !== probNegIndex && index !== probPosIndex) {
        const trimmedValue = value.trim();
        const isNumeric = !isNaN(parseFloat(trimmedValue)) && isFinite(trimmedValue);
        if (isNumeric) {
          numericColumns.add(index);
        } else {
          textColumns.add(index);
        }
      }
    });
  }
  
  
  const predictions = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    if (values.length >= headers.length) {
      const rawPrediction = parseFloat(values[predictionIndex]) || 0;
      
      // Only use patient ID if it exists in the CSV, otherwise use null
      const patientId = patientIdIndex >= 0 ? values[patientIdIndex] : null;
      
      const probNeg = probNegIndex >= 0 ? parseFloat(values[probNegIndex]) : null;
      const probPos = probPosIndex >= 0 ? parseFloat(values[probPosIndex]) : null;
      
      // Determine if this is a classification or regression model
      const hasProbabilities = probNeg !== null && probPos !== null;
      const isBinaryPrediction = rawPrediction === 0 || rawPrediction === 1;
      
      let predictionClass, confidenceScore;
      
      if (hasProbabilities) {
        // Classification model with probabilities
        predictionClass = probPos > probNeg ? 'True' : 'False';
        confidenceScore = Math.max(probPos, probNeg);
      } else if (isBinaryPrediction) {
        // Binary prediction (0/1)
        predictionClass = rawPrediction === 1 ? 'True' : 'False';
        confidenceScore = 0.8; // Default confidence for binary predictions
      } else {
        // Regression model - use raw prediction as-is
        predictionClass = rawPrediction.toString();
        confidenceScore = null; // No confidence score for regression without probabilities
      }
      
      // Find top contributing feature
      const topFeature = featureColumns.reduce((max, featureName) => {
        const featureIndex = headers.findIndex(h => h === featureName);
        const featureValue = Math.abs(parseFloat(values[featureIndex]) || 0);
        return featureValue > max.value ? { name: featureName, value: featureValue } : max;
      }, { name: featureColumns[0] || 'Unknown', value: 0 }).name;
      
      predictions.push({
        compound_id: patientId,
        compound_id_header: headers[patientIdIndex], // Preserve the original column header name
        SMILES: 'N/A', // CSV doesn't contain SMILES, but we need it for compatibility
        prediction: predictionClass,
        confidence_score: confidenceScore,
        top_feature: topFeature,
        raw_prediction: rawPrediction,
        probability_negative_class: probNeg,
        probability_positive_class: probPos,
        // Include all feature values (handle both text and numeric columns)
        feature_values: featureColumns.reduce((acc, featureName) => {
          const featureIndex = headers.findIndex(h => h === featureName);
          if (featureIndex >= 0) {
            const value = values[featureIndex]?.trim();
            if (textColumns.has(featureIndex)) {
              // Store text values as strings
              acc[featureName] = value || '';
            } else {
              // Store numeric values as numbers
              acc[featureName] = parseFloat(value) || 0;
            }
          }
          return acc;
        }, {})
      });
    }
  }
  
  return predictions;
};

// Annotation-related API calls
export const annotationAPI = {
  // Create or update an annotation
  createAnnotation: (annotation) => 
    apiRequest('/annotations', {
      method: 'POST',
      body: JSON.stringify(annotation),
    }),

  // Get annotation for a specific compound
  getAnnotation: (compoundId) => 
    apiRequest(`/annotations/${compoundId}`),

  // Get all annotations
  getAllAnnotations: () => 
    apiRequest('/annotations'),

  // Delete annotation for a specific compound
  deleteAnnotation: (compoundId) => 
    apiRequest(`/annotations/${compoundId}`, {
      method: 'DELETE',
    }),
};

// Feedback-related API calls
export const feedbackAPI = {
  // Submit feedback
  submitFeedback: (feedback) => 
    apiRequest('/feedback/submit', {
      method: 'POST',
      body: JSON.stringify(feedback),
    }),

  // Get feedback list
  getFeedbackList: (status, feedbackType) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (feedbackType) params.append('feedback_type', feedbackType);
    const query = params.toString();
    return apiRequest(`/feedback/list${query ? `?${query}` : ''}`);
  },

  // Get specific feedback item
  getFeedbackItem: (feedbackId) => 
    apiRequest(`/feedback/${feedbackId}`),

  // Update feedback status
  updateFeedbackStatus: (feedbackId, status, adminNotes) => {
    const params = new URLSearchParams({ status });
    if (adminNotes) params.append('admin_notes', adminNotes);
    return apiRequest(`/feedback/${feedbackId}/status?${params}`, {
      method: 'PUT',
    });
  },

  // Delete feedback
  deleteFeedback: (feedbackId) => 
    apiRequest(`/feedback/${feedbackId}`, {
      method: 'DELETE',
    }),

  // Get feedback statistics
  getFeedbackStats: () => 
    apiRequest('/feedback/stats/summary'),
};

// Visualization-related API calls
export const visualizationAPI = {
  // Get visualization data
  getVisualization: (datasetName, modelType, visualizationType) => {
    const params = new URLSearchParams({
      dataset_name: datasetName,
      model_type: modelType,
      visualization_type: visualizationType,
    });
    return apiRequest(`/omics/visualizations?${params}`);
  },

  // Get visualization image URL (legacy by modelType)
  getVisualizationUrl: (type, datasetName, modelType = null) => {
    const baseUrl = API_BASE_URL;
    const timestamp = Date.now(); // Add cache-busting timestamp

    switch (type) {
      case "correlation-heatmap":
        return `${baseUrl}/visualizations/correlation-heatmap/${datasetName}?t=${timestamp}`;
      case "distributions":
        return `${baseUrl}/visualizations/distributions/${datasetName}?t=${timestamp}`;
      case "feature-importance":
        return `${baseUrl}/visualizations/feature-importance/${datasetName}/${modelType}?t=${timestamp}`;
      case "model-performance":
        return `${baseUrl}/visualizations/model-performance/${datasetName}/${modelType}?t=${timestamp}`;
      case "roc-curve":
        return `${baseUrl}/visualizations/roc-curve/${datasetName}/${modelType}?t=${timestamp}`;
      case "confusion-matrix":
        return `${baseUrl}/visualizations/confusion-matrix/${datasetName}/${modelType}?t=${timestamp}`;
      case "cluster-scatter":
        return `${baseUrl}/visualizations/cluster-scatter/${datasetName}?t=${timestamp}`;
      default:
        return null;
    }
  },

  getClusterScatterUrl: (datasetName) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/cluster-scatter/${encodeURIComponent(datasetName)}?t=${timestamp}`;
  },

  // Download URL for a full correlation-matrix CSV artifact (from explore's correlation_info.artifact_filename)
  getCorrelationDownloadUrl: (filename) =>
    `${API_BASE_URL}/omics/correlations/download/${encodeURIComponent(filename)}`,

  // New: Model-specific visualization URLs by modelId
  getRocImageUrlByModelId: (datasetName, modelId) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/${datasetName}/${modelId}/roc?t=${timestamp}`;
  },
  getConfusionImageUrlByModelId: (datasetName, modelId) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/${datasetName}/${modelId}/confusion?t=${timestamp}`;
  },
  // Model-specific FI/MP URLs by modelId
  getFeatureImportanceImageUrlByModelId: (datasetName, modelId) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/${datasetName}/${modelId}/feature-importance?t=${timestamp}`;
  },
  getModelPerformanceImageUrlByModelId: (datasetName, modelId) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/${datasetName}/${modelId}/model-performance?t=${timestamp}`;
  },
  getTrainingCurvesImageUrlByModelId: (datasetName, modelId) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/${datasetName}/${modelId}/training-curves?t=${timestamp}`;
  },

  // Get instance comparison plot URL
  getInstanceComparisonImageUrlByModelId: (datasetName, modelId) => {
    const timestamp = Date.now();
    return `${API_BASE_URL}/visualizations/${datasetName}/${modelId}/ad-instance-comparison?t=${timestamp}`;
  },
  // Get correlation matrix data as JSON
  getCorrelationData: (datasetName) =>
    apiRequest(`/visualizations/correlation-heatmap/${datasetName}/json`),

  // Get distributions data as JSON
  getDistributionsData: (datasetName) =>
    apiRequest(`/visualizations/distributions/${datasetName}/json`),

  // Feature Importance / Model Performance JSON artifacts
  getFeatureImportanceJson: (datasetName, modelType) =>
    apiRequest(`/visualizations/feature-importance/${datasetName}/${modelType}/json`),
  getModelPerformanceJson: (datasetName, modelType) =>
    apiRequest(`/visualizations/model-performance/${datasetName}/${modelType}/json`),

  // New: Model-specific visualization data
  getRocDataByModelId: (datasetName, modelId) =>
    apiRequest(`/visualizations/${datasetName}/${modelId}/roc.json`),
  getConfusionDataByModelId: (datasetName, modelId) =>
    apiRequest(`/visualizations/${datasetName}/${modelId}/confusion.json`),
  getFeatureImportanceDataByModelId: (datasetName, modelId) =>
    apiRequest(`/visualizations/${datasetName}/${modelId}/feature-importance.json`),
  getModelPerformanceDataByModelId: (datasetName, modelId) =>
    apiRequest(`/visualizations/${datasetName}/${modelId}/model-performance.json`),

  // Generate ROC curve visualization (legacy by modelType)
  generateROCCurve: (datasetName, modelType) =>
    apiRequest(
      `/visualizations/generate/roc-curve/${datasetName}/${modelType}`,
      { method: "POST" }
    ),

  // New: Generate ROC curve visualization by modelId
  generateRocByModelId: (datasetName, modelId) =>
    apiRequest(`/visualizations/generate/${datasetName}/${modelId}/roc`, {
      method: "POST",
    }),

  // Generate confusion matrix visualization (legacy by modelType)
  generateConfusionMatrix: (datasetName, modelType) =>
    apiRequest(
      `/visualizations/generate/confusion-matrix/${datasetName}/${modelType}`,
      { method: "POST" }
    ),

  // New: Generate confusion matrix visualization by modelId
  generateConfusionByModelId: (datasetName, modelId) =>
    apiRequest(`/visualizations/generate/${datasetName}/${modelId}/confusion`, {
      method: "POST",
    }),

  // Generate correlation heatmap
  generateCorrelationHeatmap: (datasetName) =>
    apiRequest(`/visualizations/generate/correlation-heatmap/${datasetName}`, {
      method: "POST",
    }),

  // Generate feature importance plot
  generateFeatureImportance: (datasetName, modelType) =>
    apiRequest(
      `/visualizations/generate/feature-importance/${datasetName}/${modelType}`,
      { method: "POST" }
    ),

  // Generate model performance plot
  generateModelPerformance: (datasetName, modelType) =>
    apiRequest(
      `/visualizations/generate/model-performance/${datasetName}/${modelType}`,
      { method: "POST" }
    ),
};

// Model Registry API calls
export const modelRegistryAPI = {
  // Get registry information
  getRegistryInfo: () => 
    apiRequest('/omics/registry/info'),

  // List saved models
  listSavedModels: (datasetName, modelType, includeDeleted = false) => {
    const params = new URLSearchParams();
    if (datasetName) params.append('dataset_name', datasetName);
    if (modelType) params.append('model_type', modelType);
    if (includeDeleted) params.append('include_deleted', 'true');
    const query = params.toString();
    return apiRequest(`/omics/registry/models${query ? `?${query}` : ''}`);
  },

  // Get specific model info
  getModelInfo: (modelId) => 
    apiRequest(`/omics/registry/models/${modelId}`),

  // Delete a model
  deleteModel: (modelId) => 
    apiRequest(`/omics/registry/models/${modelId}`, { method: 'DELETE' }),

  // Cleanup old models
  cleanupOldModels: (daysOld = 30) => 
    apiRequest(`/omics/registry/cleanup?days_old=${daysOld}`, { method: 'POST' }),

  // Get complete models registry
  getModelsRegistry: () => 
    apiRequest('/omics/models'),

  // Get model preprocess log
  getPreprocessLog: (modelId) => 
    apiRequest(`/omics/models/${modelId}/preprocess-log`),

  // Decision threshold (binary classification; changing it does not require retraining)
  getDecisionThreshold: (modelId) => 
    apiRequest(`/omics/models/${modelId}/decision-threshold`),
  updateDecisionThreshold: (modelId, decisionThreshold) => 
    apiRequest(`/omics/models/${modelId}/decision-threshold`, {
      method: 'PATCH',
      body: JSON.stringify({ decision_threshold: decisionThreshold }),
    }),

  // Registry health and validation endpoints
  getRegistryHealth: () => 
    apiRequest('/omics/registry/health'),

  validateAllModels: () => 
    apiRequest('/omics/registry/validate', { method: 'POST' }),

  validateModelById: (modelId) =>
    apiRequest(`/omics/registry/models/${modelId}/validate`, { method: 'POST' }),

  // Run the independent AD-association benchmark on a saved model
  runAdBenchmark: (modelId, options = {}) =>
    apiRequest(`/omics/registry/models/${modelId}/ad-benchmark`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),

  repairRegistry: () => 
    apiRequest('/omics/registry/repair', { method: 'POST' }),

  removeInvalidModels: () => 
    apiRequest('/omics/registry/invalid', { method: 'DELETE' }),

  // Make predictions with saved model
  predictWithModel: (modelId, inputData) => 
    apiRequest(`/omics/predict?model_id=${encodeURIComponent(modelId)}`, {
      method: 'POST',
      body: JSON.stringify(inputData),
    }),

  // Make batch predictions with saved model
  predictBatchWithModel: (modelId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return apiRequest(`/omics/predict/batch?model_id=${modelId}`, {
      method: 'POST',
      body: formData,
    });
  },

  // Restore a soft-deleted model
  restoreModel: (modelId) => 
    apiRequest(`/omics/registry/models/${modelId}/restore`, { method: 'POST' }),

  // Permanently delete a model
  hardDeleteModel: (modelId) => 
    apiRequest(`/omics/registry/models/${modelId}/hard-delete`, { method: 'DELETE' }),

  // Get multiple models for comparison (fetches details in parallel)
  getModelsForComparison: (modelIds) => 
    Promise.all(modelIds.filter(Boolean).map(id => 
      apiRequest(`/omics/registry/models/${id}`).catch(() => null)
    )),
};

// Export API calls
export const exportAPI = {
  // Get available exports for a model
  getAvailableExports: (modelId) => 
    apiRequest(`/results/${modelId}/export/available`),

  // Export single visualization or comprehensive report
  exportVisualization: async (modelId, visualization = 'all', format = 'pdf') => {
    const params = new URLSearchParams();
    params.append('visualization', visualization);
    params.append('format', format);
    
    const url = `${API_BASE_URL}/results/${modelId}/export?${params.toString()}`;
    const token = getAuthToken();
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.detail || `Failed to export: ${response.status}`);
    }
    
    // Return blob for download
    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')
      ?.split('filename=')[1]
      ?.replace(/"/g, '') 
      || `export_${modelId}_${visualization}.${format}`;
    
    return { blob, filename };
  },

  // Export comprehensive report (convenience method)
  exportComprehensiveReport: async (modelId) => {
    const url = `${API_BASE_URL}/results/${modelId}/export/comprehensive`;
    const token = getAuthToken();
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.detail || `Failed to export report: ${response.status}`);
    }
    
    // Return blob for download
    const blob = await response.blob();
    const filename = response.headers.get('Content-Disposition')
      ?.split('filename=')[1]
      ?.replace(/"/g, '') 
      || `model_${modelId}_report.pdf`;
    
    return { blob, filename };
  },

  // Helper function to trigger download
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// Pathway API calls
export const pathwayAPI = {
  // Get pathway information for a list of genes
  enrichPathways: (genes, topN = 5) =>
    apiRequest('/pathway/enrich', {
      method: 'POST',
      body: JSON.stringify({ genes, top_n: topN }),
    }),
  senescencePathways: (genes, topN = 5) =>
    apiRequest('/pathway/senescence', {
      method: 'POST',
      body: JSON.stringify({ genes, top_n: topN }),
    }),
  unifiedEnrichPathways: (genes, topN = 5) =>
    apiRequest('/pathway/unified-enrich', {
      method: 'POST',
      body: JSON.stringify({ genes, top_n: topN }),
    }),
};

// Open Targets API calls
export const targetAPI = {
  // Get clinical annotations, drug tractability, and disease association scores
  // efoId defaults to "MONDO_0004975" (Alzheimer's disease) but can be overridden to query for other diseases
  getTargetAnnotations: (genes, efoId = "MONDO_0004975") =>
    apiRequest('/target/annotations', {
      method: 'POST',
      body: JSON.stringify({ genes, efo_id: efoId }),
    }),

  scoreTargets: (genes, featureImportance = null, efoId = 'MONDO_0004975') =>
    apiRequest('/target/score', {
      method: 'POST',
      body: JSON.stringify({
        genes,
        efo_id: efoId,
        feature_importance: featureImportance,
      }),
    }),
};

// Export individual functions for backward compatibility
export const {
  getDatasets,
  previewDataset,
  exploreDataset,
  analyzeCorrelations,
  normalizeData,
  deleteDataset,
  renameDataset,
} = datasetsAPI;

export const {
  validateTrainingData,
  trainMLModel,
  getFeatureImportance,
  getModelPerformance,
  getFeatureImportanceData,
  getTargetScorecards,
  getEffectiveGenes,
  scoreTargets,
  getModelPerformanceData,
  getTrainingStatus,
  getModelSummary,
  getEvaluationFlags,
  runKMeansClustering,
  recommendClusterK,
} = mlModelAPI;

export const {
  uploadFile,
} = uploadAPI;

export const {
  getCompoundDetails,
} = compoundAPI;

export const {
  getPredictions,
  getLatestPredictionResults,
  getPredictionResultsFromFile,
} = predictAPI;

export const {
  createAnnotation,
  getAnnotation,
  getAllAnnotations,
  deleteAnnotation,
} = annotationAPI;

export const {
  submitFeedback,
  getFeedbackList,
  getFeedbackItem,
  updateFeedbackStatus,
  deleteFeedback,
  getFeedbackStats,
} = feedbackAPI;

export const {
  getVisualization,
  getVisualizationUrl,
  getClusterScatterUrl,
  getCorrelationDownloadUrl,
  getCorrelationData,
  getDistributionsData,
  getFeatureImportanceJson,
  getModelPerformanceJson,
  getRocImageUrlByModelId,
  getConfusionImageUrlByModelId,
  getFeatureImportanceImageUrlByModelId,
  getModelPerformanceImageUrlByModelId,
  getTrainingCurvesImageUrlByModelId,
  getRocDataByModelId,
  getConfusionDataByModelId,
  getFeatureImportanceDataByModelId,
  getModelPerformanceDataByModelId,
  generateRocByModelId,
  generateConfusionByModelId,
  generateROCCurve,
  generateConfusionMatrix,
  generateCorrelationHeatmap,
  generateFeatureImportance,
  generateModelPerformance,
  getInstanceComparisonImageUrlByModelId,
} = visualizationAPI;

export const {
  getRegistryInfo,
  listSavedModels,
  getModelInfo,
  getPreprocessLog,
  getDecisionThreshold,
  updateDecisionThreshold,
  deleteModel,
  cleanupOldModels,
  getModelsRegistry,
  getRegistryHealth,
  validateAllModels,
  repairRegistry,
  removeInvalidModels,
  predictWithModel,
  predictBatchWithModel,
  restoreModel,
  hardDeleteModel,
  getModelsForComparison,
} = modelRegistryAPI;

export const {
  getAvailableExports,
  exportVisualization,
  exportComprehensiveReport,
  downloadFile,
} = exportAPI;

export const {
  getTargetAnnotations,
} = targetAPI;

// Artifact Jobs API calls
export const artifactJobsAPI = {
  // Submit a job - resolves to the initial job status
  submitJob: (jobType, params = {}) =>
    apiRequest('/artifacts/jobs', {
      method: 'POST',
      body: JSON.stringify({ job_type: jobType, params }),
    }),

  // Poll a single job's status/progress.
  getJob: (jobId) => apiRequest(`/artifacts/jobs/${encodeURIComponent(jobId)}`),

  // Request cooperative cancellation; resolves to the refreshed status.
  cancelJob: (jobId) =>
    apiRequest(`/artifacts/jobs/${encodeURIComponent(jobId)}/cancel`, { method: 'POST' }),

  // Fetch the finished artifact as a blob (sends auth header) and trigger a browser download.
  downloadResult: async (jobId, fallbackName = 'artifact') => {
    const url = `${API_BASE_URL}/artifacts/jobs/${encodeURIComponent(jobId)}/result`;
    const token = getAuthToken();
    const response = await fetch(url, {
      method: 'GET',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.detail || `Failed to download artifact: ${response.status}`);
    }
    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') ||
      fallbackName;
    downloadFile(blob, filename);
    return filename;
  },

  // Submit then poll to a terminal state, reporting progress along the way.
  // onProgress(jobStatus) is called after each poll. Returns the final job status.
  runJob: async (jobType, params = {}, { onProgress, pollIntervalMs = 800, timeoutMs = 300000 } = {}) => {
    const submitted = await artifactJobsAPI.submitJob(jobType, params);
    const jobId = submitted.job_id;
    if (onProgress) onProgress(submitted);

    const terminal = new Set(['completed', 'failed', 'cancelled']);
    const started = Date.now();
    let status = submitted;

    while (!terminal.has(status.state)) {
      if (Date.now() - started > timeoutMs) {
        throw new Error('Timed out waiting for the artifact job to finish.');
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      status = await artifactJobsAPI.getJob(jobId);
      if (onProgress) onProgress(status);
    }

    if (status.state === 'failed') {
      throw new Error(status.error || 'Artifact job failed.');
    }
    return status;
  },
};